/** Pure size-targeting logic for the client-side re-encode pipeline (unit tested). */

/** Longest edge after downscale. Keeps JPEGs frame-friendly and well under email limits. */
export const MAX_EDGE_PX = 2500;

/** Target output size (~2 MB) so emailed photos stay far below ACS's ~10 MB cap. */
export const TARGET_IMAGE_BYTES = 2 * 1024 * 1024;

export interface Dimensions {
  width: number;
  height: number;
}

/**
 * Compute output dimensions: preserve aspect ratio, cap the longest edge,
 * never upscale, always return at least 1×1.
 */
export function computeTargetDimensions(
  width: number,
  height: number,
  maxEdge: number = MAX_EDGE_PX
): Dimensions {
  if (width <= 0 || height <= 0) return { width: 1, height: 1 };
  const longest = Math.max(width, height);
  if (longest <= maxEdge) return { width: Math.round(width), height: Math.round(height) };
  const scale = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale))
  };
}

/**
 * Output dimensions for the 'framefill' style: a canvas AT the frame's aspect
 * ratio (`targetRatio` = width/height), sized within the same compression
 * budget as the normal pipeline (longest edge capped at `maxEdge`, never
 * upscaled). The source is center-cropped (cover) to fill this canvas, so the
 * frame displays the whole composite — photo + caption — with nothing to crop.
 *
 * The natural (uncapped) size is the center-crop rectangle taken from the
 * source at 1:1, so we never invent pixels; capping the longest edge keeps the
 * encoded JPEG near the ~2 MB target like every other output.
 */
export function computeFramefillDimensions(
  srcWidth: number,
  srcHeight: number,
  targetRatio: number,
  maxEdge: number = MAX_EDGE_PX
): Dimensions {
  if (srcWidth <= 0 || srcHeight <= 0 || !(targetRatio > 0) || !Number.isFinite(targetRatio)) {
    return { width: 1, height: 1 };
  }
  const srcRatio = srcWidth / srcHeight;
  // The largest rectangle of ratio `targetRatio` that fits inside the source.
  let cropW: number;
  let cropH: number;
  if (srcRatio > targetRatio) {
    // Source is wider than target → full height, crop left/right.
    cropH = srcHeight;
    cropW = srcHeight * targetRatio;
  } else {
    // Source is taller/narrower → full width, crop top/bottom.
    cropW = srcWidth;
    cropH = srcWidth / targetRatio;
  }
  return computeTargetDimensions(cropW, cropH, maxEdge);
}

/** A source rectangle to sample when drawing (drawImage sx,sy,sw,sh). */
export interface CoverCrop {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
}

/**
 * Center "cover" crop: the largest source rectangle whose aspect ratio matches
 * the destination canvas, centered. Used by 'framefill' so the photo fills the
 * frame-aspect canvas edge-to-edge (overflowing dimension cropped equally on
 * both sides). Deliberately simple/center — no subject detection.
 */
export function coverCrop(
  srcWidth: number,
  srcHeight: number,
  dstWidth: number,
  dstHeight: number
): CoverCrop {
  if (srcWidth <= 0 || srcHeight <= 0 || dstWidth <= 0 || dstHeight <= 0) {
    return { sx: 0, sy: 0, sw: Math.max(1, srcWidth), sh: Math.max(1, srcHeight) };
  }
  const srcRatio = srcWidth / srcHeight;
  const dstRatio = dstWidth / dstHeight;
  let sw: number;
  let sh: number;
  if (srcRatio > dstRatio) {
    // Source wider → crop the sides.
    sh = srcHeight;
    sw = srcHeight * dstRatio;
  } else {
    // Source taller → crop top/bottom.
    sw = srcWidth;
    sh = srcWidth / dstRatio;
  }
  return { sx: (srcWidth - sw) / 2, sy: (srcHeight - sh) / 2, sw, sh };
}

export interface EncodeAttempt {
  /** Additional scale applied on top of the MAX_EDGE downscale (1 = none). */
  scale: number;
  /** JPEG quality 0..1. */
  quality: number;
}

/**
 * The ordered ladder of encode attempts. We first step quality down, then also
 * shrink dimensions for stubborn images. The first attempt whose output is
 * within TARGET_IMAGE_BYTES wins; the final attempt is accepted unconditionally.
 */
export const ENCODE_ATTEMPTS: readonly EncodeAttempt[] = [
  { scale: 1, quality: 0.85 },
  { scale: 1, quality: 0.78 },
  { scale: 1, quality: 0.7 },
  { scale: 1, quality: 0.6 },
  { scale: 1, quality: 0.5 },
  { scale: 0.8, quality: 0.6 },
  { scale: 0.65, quality: 0.55 },
  { scale: 0.5, quality: 0.5 }
];

/** Accept an encoded blob if it hits the target — or if we're out of attempts. */
export function isAcceptableSize(
  bytes: number,
  attemptIndex: number,
  targetBytes: number = TARGET_IMAGE_BYTES
): boolean {
  if (bytes <= targetBytes) return true;
  return attemptIndex >= ENCODE_ATTEMPTS.length - 1;
}

/** Generated, non-user-supplied upload names (SECURITY.md T6): 1.jpg, 2.mp4, ... */
export function uploadFileName(index: number, kind: "image" | "video", originalName: string): string {
  if (kind === "image") return `${index + 1}.jpg`;
  const dot = originalName.lastIndexOf(".");
  const rawExt = dot >= 0 ? originalName.slice(dot + 1).toLowerCase() : "";
  const ext = /^[a-z0-9]{1,5}$/.test(rawExt) ? rawExt : "mp4";
  return `${index + 1}.${ext}`;
}
